import { Component, OnInit } from '@angular/core';
import moment from 'moment';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CustomValidators } from 'src/app/shared/customValidators/validator';
import {
  VatService,
  InvoiceService,
  InvoiceLineItemService,
  CreditNoteService,
  CmReasonService,
} from 'src/app/api/invoice/services';
import {
  BusinessType,
  CreditType,
  InvoiceDetailDto,
  InvoiceDto,
  InvoiceLineItemDto,
  VatDto,
  GetCmReasonResponse,
  CmReasonDto,
  CreateCreditNoteRequest,
} from 'src/app/api/invoice/models';
import { IListParams } from 'src/app/shared/models/user.model';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { RouteService } from 'src/app/shared/services/routeService';
import { RedirectService } from 'src/app/shared/services/redirect-service';
import { PERMISSION_CONSTANTS } from 'src/app/shared/models/permission-constants';
import { CONSTANTS } from 'src/app/shared/models/constants';
import { numberPattern } from '../../models/validation-rules';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { businessTypes } from '../../models/business-types';
@Component({
  selector: 'app-common-partial-credit-note',
  templateUrl: './common-partial-credit-note.component.html',
  styleUrls: ['./common-partial-credit-note.component.scss'],
})
export class CommonPartialCreditNoteComponent implements OnInit {
  invoiceForm: FormGroup;
  invoiceDetails: InvoiceDetailDto = {};
  lineIeItemDetails: InvoiceLineItemDto[] = [];
  invoiceId = '';
  module = '';
  partialCreditLoader = false;
  vatOptions: VatDto[] = [];
  creditNoteVatOptions: VatDto[] = [];
  permissionConstants = PERMISSION_CONSTANTS;
  defaultVatPercentage = 15;
  defaultVat: VatDto = {};
  constants = CONSTANTS;
  isFlightPrice = true;
  businessType: BusinessType = BusinessType.SalesOrder;
  reasonOptions: CmReasonDto[] = [];
  totalExcludingVat = 0;
  totalOutOfScope = 0;
  totalIncludingVat = 0;
  totalVat = 0;
  totalAmount = 0;
  totalTaxableAmount = 0;
  decimalPattern = /^\d+(\.\d+)?$/;
  taxableAmount!: number;
  creditStatus = '';
  totalExVat!: number;
  minDate: any;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private invoiceService: InvoiceService,
    private lineItemService: InvoiceLineItemService,
    private creditNoteService: CreditNoteService,
    private cmReasonService: CmReasonService,
    private vatService: VatService,
    private toastr: ToastrService,
    private location: Location,
    private routeService: RouteService,
    private router: Router,
    public dialog: MatDialog,
    public redirectService: RedirectService
  ) {
    this.invoiceForm = this.fb.group({
      serviceDate: [null, [Validators.required]],
      ebsReason: [null, [Validators.required]],
      zatcaReason: ['', [Validators.required]],
      lineItems: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.loadCmReasons();
    this.route.queryParams.subscribe(params => {
      this.module = params['businessType'];
      this.invoiceId = params['referralInvoice'];

      if (this.invoiceId) {
        this.loadInvoiceDetails();
        this.getInvoiceLineItems();
      }
    });

    this.fetchVat({ page: 0, size: 300, search: '' });
  }

  get lineItems(): FormArray {
    return this.invoiceForm.get('lineItems') as FormArray;
  }
  shouldAllowQuantityEdit(): boolean {
    const editableModules = [
      'MCC',
      'TRAINING',
      'FINANCE',
      'SALES_ORDER',
      'MEDVAC',
      'ACM_PARTIAL',
      'AIRPORT_MANAGEMENT_SERVICE',
    ];
    return editableModules.includes(this.module);
  }

  isFlightPriceItem(item: any): boolean {
    return item.value.description?.trim()?.toLowerCase() === 'flight price';
  }

  isFinanceCreditNote(): boolean {
    return this.module === 'FINANCE';
  }

  canEditCreditNoteVat(): boolean {
    return (
      this.isFinanceCreditNote() &&
      this.redirectService.hasPermission(
        this.permissionConstants.EDIT_FINANCE_CREDIT_NOTE_VAT
      )
    );
  }

  formatVatDisplay(vat: string | VatDto | null | undefined): string {
    const resolved = this.resolveVatFromLineItem(vat);
    if (resolved?.percentage != null) {
      return `${resolved.percentage}%`;
    }
    if (vat != null && vat !== '') {
      const raw = String(vat).trim();
      return raw.includes('%') ? raw : `${raw}%`;
    }
    return '';
  }

  loadCmReasons(): void {
    this.cmReasonService
      .getAllCmReasons({ page: 0, size: 100, sort: 'id,asc' })
      .subscribe({
        next: (res: GetCmReasonResponse) => {
          this.reasonOptions = res?.data?.[0]?.data ?? [];
        },
        error: err => console.error('Failed to load CM reasons', err),
      });
  }
  private createLineItem(item: any): FormGroup {
    this.isFlightPrice = item?.description
      ?.trim()
      .toLowerCase()
      .includes('flight price');
    const group = this.fb.group(
      {
        isActive: [true],
        lineItemId: [item?.lineItemId || ''],
        description: [item?.description || ''],
        quantity: [item?.quantity || ''],
        unitPrice: [item?.unitPrice || ''],
        vat: [
          this.isFinanceCreditNote()
            ? this.resolveVatFromLineItem(item?.vat)
            : item?.vat,
        ],
        creditedQuantity: [
          { value: item.quantity, disabled: !this.shouldAllowQuantityEdit() },
          this.isFlightPrice
            ? []
            : this.module === 'AIRPORT_MANAGEMENT_SERVICE'
            ? [
                Validators.required,
                Validators.pattern(this.decimalPattern),
                Validators.min(0.01),
              ]
            : [
                Validators.required,
                Validators.pattern(/^\d+$/),
                Validators.min(1),
              ],
        ],
        creditedUnitPrice: [
          { value: item.unitPrice, disabled: false },
          [
            Validators.required,
            Validators.pattern(numberPattern),
            Validators.min(1),
          ],
        ],
      },
      { validators: [CustomValidators.creditedValuesWithinOriginal()] }
    );
    return group;
  }

  loadInvoiceDetails(): void {
    const params = { id: parseInt(this.invoiceId) };
    this.invoiceService.getInvoiceById(params).subscribe({
      next: res => {
        if (res?.data?.[0]) {
          this.invoiceDetails = res.data[0];
          this.minDate = this.invoiceDetails.serviceDate
            ? moment(this.invoiceDetails.serviceDate).toDate()
            : null;
          // this.setLineItems();
          this.creditStatus = res?.data[0]?.creditStatusGIS || '';

          if (this.module === 'ACM_PARTIAL') {
            this.totalExVat =
              this.invoiceDetails.serviceOrder?.totalExcludingVat || 0;
          } else {
            this.totalExVat =
              this.invoiceDetails.quotation?.totalExcludingVat || 0;
          }
          this.getTaxableAmount();
        }
      },
      error: err => this.toastr.error(err?.error?.message),
    });
  }
  getTaxableAmount() {
    if (this.module === 'ACM_PARTIAL') {
      const totalExcludingVat =
        this.invoiceDetails.serviceOrder?.totalExcludingVat ?? 0;
      const totalOutOfScope =
        this.invoiceDetails.serviceOrder?.totalOutOfScope ?? 0;
      this.taxableAmount = totalExcludingVat - totalOutOfScope;
    } else {
      const totalExcludingVat =
        this.invoiceDetails.quotation?.totalExcludingVat ?? 0;
      const totalOutOfScope =
        this.invoiceDetails.quotation?.totalOutOfScope ?? 0;
      this.taxableAmount = totalExcludingVat - totalOutOfScope;
    }
  }

  getInvoiceLineItems() {
    this.lineItemService
      .getInvoiceLineItems({ id: +this.invoiceId })
      .subscribe({
        next: res => {
          if (res?.data?.[0]) {
            this.lineIeItemDetails = res?.data;
            this.setLineItems();
          }
        },
        error: err => this.toastr.error(err?.error?.message),
      });
  }

  setLineItems(): void {
    const items = this.lineIeItemDetails || [];
    this.lineItems.clear();

    items.forEach((item: any) => {
      this.lineItems.push(this.createLineItem(item));
    });
    this.patchLineItemVats();
  }

  fetchVat(params: IListParams): void {
    this.vatService.getAllVat(params).subscribe({
      next: response => {
        this.vatOptions = response?.data || [];
        this.buildCreditNoteVatOptions(this.vatOptions);
        const defaultVat = this.vatOptions.find(
          v => v.percentage === this.defaultVatPercentage
        );
        this.defaultVat = defaultVat || {};
        this.patchLineItemVats();
      },
      error: err => this.toastr.error(err?.error?.message),
    });
  }

  private buildCreditNoteVatOptions(allVat: VatDto[]): void {
    this.creditNoteVatOptions = allVat
      .filter(v => v.percentage === 0 || v.percentage === 15)
      .sort((a, b) => (a.percentage ?? 0) - (b.percentage ?? 0));
  }

  private resolveVatFromLineItem(
    vat: string | VatDto | null | undefined
  ): VatDto | null {
    if (vat == null || vat === '') {
      return (
        this.creditNoteVatOptions.find(v => v.percentage === 15) ??
        this.defaultVat ??
        null
      );
    }

    if (typeof vat === 'object') {
      if (vat.id) {
        return (
          this.creditNoteVatOptions.find(v => v.id === vat.id) ??
          (vat.percentage === 0 || vat.percentage === 15 ? vat : null)
        );
      }
      if (vat.percentage === 0 || vat.percentage === 15) {
        return (
          this.creditNoteVatOptions.find(
            v => v.percentage === vat.percentage
          ) ?? vat
        );
      }
    }

    const percentage = Number(String(vat).replace(/[^\d.]/g, ''));
    if (!Number.isNaN(percentage)) {
      return (
        this.creditNoteVatOptions.find(v => v.percentage === percentage) ?? null
      );
    }

    return null;
  }

  private patchLineItemVats(): void {
    if (!this.isFinanceCreditNote()) {
      this.calculateTotals();
      return;
    }

    if (!this.creditNoteVatOptions.length || !this.lineItems.length) {
      return;
    }

    this.lineItems.controls.forEach((control, index) => {
      const rawVat =
        this.lineIeItemDetails[index]?.vat ?? control.get('vat')?.value;
      const resolved = this.resolveVatFromLineItem(rawVat);
      if (resolved) {
        control.get('vat')?.patchValue(resolved, { emitEvent: false });
      }
    });
    this.calculateTotals();
  }

  resolveVatId(vat: string | VatDto | null | undefined): number | undefined {
    return this.resolveVatFromLineItem(vat)?.id;
  }

  calculateTotals(): void {
    let totalExcludingVat = 0;
    let totalOutOfScope = 0;
    let totalTaxableAmount = 0;
    let totalVatAmount = 0;
    let totalAmount = 0;

    this.lineItems.controls.forEach(control => {
      const isActive = control.get('isActive')?.value;
      if (!isActive) {
        return;
      }

      const qtyRaw = control.get('creditedQuantity')?.value;
      const priceRaw = control.get('creditedUnitPrice')?.value;
      const vatObj = control.get('vat')?.value;
      const qty = Number(String(qtyRaw ?? '').replace(/,/g, '')) || 0;
      const unitPrice = Number(String(priceRaw ?? '').replace(/,/g, '')) || 0;
      const vatPercentage = Number(vatObj?.percentage ?? vatObj ?? 0) || 0;
      const net = qty * unitPrice;
      const vatAmount = net * (vatPercentage / 100);
      const lineTotal = net + vatAmount;
      totalExcludingVat += net;
      if (vatPercentage === 0) {
        totalOutOfScope += net;
      } else {
        totalTaxableAmount += net;
      }
      totalVatAmount += vatAmount;
      totalAmount += lineTotal;
    });
    this.totalExcludingVat = totalExcludingVat;
    this.totalOutOfScope = totalOutOfScope;
    this.totalTaxableAmount = totalTaxableAmount;
    this.totalVat = totalVatAmount;
    this.totalAmount = totalAmount;
  }

  getTotalTaxableAmount(): number {
    return this.totalTaxableAmount;
  }

  onSubmit(): void {
    if (this.invoiceForm.invalid) {
      this.invoiceForm.markAllAsTouched();
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      data: {
        message:
          'By proceeding, this data will be submitted. Please confirm to continue.',
        module: this.module,
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.partialCreditLoader = true;
        const items = this.lineItems.value
          .filter(
            (item: any) =>
              (item.isActive && item.creditedQuantity !== '0') || ''
          )
          .map((item: any) => {
            const {
              creditedQuantity,
              creditedUnitPrice,
              description,
              ...rest
            } = item;

            return {
              ...rest,
              itemDescription: description,
              quantity: creditedQuantity ? creditedQuantity : item.quantity,
              unitPrice: creditedUnitPrice ? creditedUnitPrice : item.unitPrice,
              parentLineItemId: item.lineItemId,
              vatId: this.isFinanceCreditNote()
                ? item?.vat?.id ?? this.resolveVatId(item.vat)
                : item?.vat?.id,
              type: 'OTHER',
            };
          });

        const formData: CreateCreditNoteRequest = {
          creditType: CreditType.Partial,
          ebsReasonId: this.invoiceForm.get('ebsReason')?.value,
          reason: this.invoiceForm.get('zatcaReason')?.value,
          serviceDate: moment
            .parseZone(this.invoiceForm.get('serviceDate')?.value)
            .format('YYYY-MM-DD'),
          items,
          showUsd: result.showUsd,
        };

        this.creditNoteService
          .createInvoiceCreditNote({
            id: parseInt(this.invoiceId),
            body: formData,
          })
          .subscribe({
            next: response => {
              this.partialCreditLoader = false;
              this.onClickRow(response?.data?.[0] || {});
              this.toastr.success(response.message);
            },
            error: error => {
              this.partialCreditLoader = false;
              this.toastr.error(
                error?.error?.message || 'Failed to create credit note'
              );
            },
          });
      }
    });
  }

  onClickRow(row: InvoiceDto): void {
    const detailRoute = this.routeService.findRoute(this.module);
    this.router.navigate([detailRoute.detailRoute + row.id], {
      queryParams: {
        type: 'CREDIT',
        businessType: this.businessType,
        module: this.module,
      },
    });
  }

  goBack(): void {
    this.location.back();
  }
  hasAtLeastOneActiveLine(): boolean {
    return this.lineItems.controls.some(c => c.get('isActive')?.value === true);
  }

  onToggleLine(index: number) {
    const group = this.lineItems.at(index) as FormGroup;
    const isActive = group.get('isActive')?.value;
    if (this.shouldAllowQuantityEdit()) {
      if (!isActive) {
        group.get('creditedQuantity')?.disable({ emitEvent: false });
        group.get('creditedUnitPrice')?.disable({ emitEvent: false });
      } else {
        group.get('creditedUnitPrice')?.enable({ emitEvent: false });
        group.get('creditedQuantity')?.enable({ emitEvent: false });
      }
    } else {
      if (!isActive) {
        // 🔹 User striked out → disable both
        group.get('creditedQuantity')?.disable({ emitEvent: false });
        group.get('creditedUnitPrice')?.disable({ emitEvent: false });
      } else {
        // 🔹 User un-striked → re-enable
        group.get('creditedUnitPrice')?.enable({ emitEvent: false });

        // if (!isFlightPrice) {
        //   group.get('creditedQuantity')?.enable({ emitEvent: false });
        // }
      }
    }

    this.calculateTotals();
  }
}
